import { z } from 'zod';

import { Id, IsoDateTime } from '../common/primitives';

export { Paise } from '../common/primitives';

// ── Enums ──────────────────────────────────────────────────────────────────

export const CountryStatusSchema = z.enum(['ACTIVE', 'COMING_SOON', 'DISABLED']);
export type CountryStatus = z.infer<typeof CountryStatusSchema>;

export const TaxRuleKindSchema = z.enum(['DEFAULT', 'CATEGORY', 'PRODUCT']);
export type TaxRuleKind = z.infer<typeof TaxRuleKindSchema>;

// Common validators
const CountryCode = z
  .string()
  .trim()
  .regex(/^[A-Z]{2}$/, 'ISO 3166-1 alpha-2');
const CurrencyCode = z
  .string()
  .trim()
  .regex(/^[A-Z]{3}$/, 'ISO 4217');
const LocaleCode = z
  .string()
  .trim()
  .regex(/^[a-z]{2}(-[A-Z]{2})?$/, 'BCP-47 e.g. en-IN');

// ── Country ────────────────────────────────────────────────────────────────

export const CountrySchema = z.object({
  id: Id,
  code: z.string(),
  name: z.string(),
  currencyCode: z.string(),
  vatRateBps: z.number().int(),
  region: z.string().nullable(),
  defaultLocale: z.string(),
  status: CountryStatusSchema,
  createdAt: IsoDateTime,
});
export type Country = z.infer<typeof CountrySchema>;

export const UpsertCountryRequest = z
  .object({
    code: CountryCode,
    name: z.string().trim().min(2).max(120),
    currencyCode: CurrencyCode,
    vatRateBps: z.number().int().min(0).max(5000),
    region: z.string().trim().max(40).optional(),
    defaultLocale: LocaleCode.optional(),
    status: CountryStatusSchema.optional(),
  })
  .strict();
export type UpsertCountryRequest = z.infer<typeof UpsertCountryRequest>;

// ── Currency ───────────────────────────────────────────────────────────────

export const CurrencySchema = z.object({
  id: Id,
  code: z.string(),
  name: z.string(),
  symbol: z.string(),
  minorUnits: z.number().int(),
  rateToInrX10000: z.number().int(),
  rateUpdatedAt: IsoDateTime.nullable(),
  active: z.boolean(),
});
export type Currency = z.infer<typeof CurrencySchema>;

export const UpsertCurrencyRequest = z
  .object({
    code: CurrencyCode,
    name: z.string().trim().min(2).max(60),
    symbol: z.string().trim().min(1).max(8),
    minorUnits: z.number().int().min(0).max(4).optional(),
    rateToInrX10000: z.number().int().min(0).optional(),
    active: z.boolean().optional(),
  })
  .strict();
export type UpsertCurrencyRequest = z.infer<typeof UpsertCurrencyRequest>;

export const RecordFxRateRequest = z
  .object({
    fromCurrency: CurrencyCode,
    toCurrency: CurrencyCode,
    rateX10000: z.number().int().min(1),
    source: z.string().trim().max(60).optional(),
  })
  .strict();
export type RecordFxRateRequest = z.infer<typeof RecordFxRateRequest>;

export const ConvertCurrencyRequest = z
  .object({
    amountMinor: z.number().int().min(0),
    fromCurrency: CurrencyCode,
    toCurrency: CurrencyCode,
  })
  .strict();
export type ConvertCurrencyRequest = z.infer<typeof ConvertCurrencyRequest>;

export const ConversionResultSchema = z.object({
  amountMinor: z.number().int(),
  fromCurrency: z.string(),
  toCurrency: z.string(),
  rateX10000: z.number().int(),
  convertedMinor: z.number().int(),
});
export type ConversionResult = z.infer<typeof ConversionResultSchema>;

// ── Locale + Translation ──────────────────────────────────────────────────

export const LocaleSchema = z.object({
  id: Id,
  code: z.string(),
  name: z.string(),
  nativeName: z.string().nullable(),
  rtl: z.boolean(),
  active: z.boolean(),
});
export type Locale = z.infer<typeof LocaleSchema>;

export const UpsertLocaleRequest = z
  .object({
    code: LocaleCode,
    name: z.string().trim().min(2).max(60),
    nativeName: z.string().trim().max(60).optional(),
    rtl: z.boolean().optional(),
    active: z.boolean().optional(),
  })
  .strict();
export type UpsertLocaleRequest = z.infer<typeof UpsertLocaleRequest>;

export const TranslationSchema = z.object({
  id: Id,
  locale: z.string(),
  namespace: z.string(),
  key: z.string(),
  value: z.string(),
  description: z.string().nullable(),
});
export type Translation = z.infer<typeof TranslationSchema>;

export const UpsertTranslationRequest = z
  .object({
    locale: LocaleCode,
    namespace: z.string().trim().min(1).max(60).optional(),
    key: z.string().trim().min(1).max(200),
    value: z.string().trim().min(1).max(4000),
    description: z.string().trim().max(500).optional(),
  })
  .strict();
export type UpsertTranslationRequest = z.infer<typeof UpsertTranslationRequest>;

export const TranslateQuery = z
  .object({
    locale: LocaleCode,
    namespace: z.string().trim().min(1).max(60).optional(),
    keys: z.array(z.string().trim().min(1).max(200)).min(1).max(200),
  })
  .strict();
export type TranslateQuery = z.infer<typeof TranslateQuery>;

export const TranslationLookupResultSchema = z.object({
  locale: z.string(),
  /// Final resolution order applied: ["fr-FR", "fr", "en-IN"].
  resolvedFallbacks: z.array(z.string()),
  values: z.record(z.string()),
});
export type TranslationLookupResult = z.infer<typeof TranslationLookupResultSchema>;

// ── Tax ────────────────────────────────────────────────────────────────────

export const TaxRuleSchema = z.object({
  id: Id,
  countryCode: z.string(),
  kind: TaxRuleKindSchema,
  hsnSac: z.string().nullable(),
  variantId: Id.nullable(),
  rateBps: z.number().int(),
  inclusive: z.boolean(),
  label: z.string().nullable(),
  active: z.boolean(),
  validFrom: IsoDateTime,
  validTo: IsoDateTime.nullable(),
});
export type TaxRule = z.infer<typeof TaxRuleSchema>;

export const UpsertTaxRuleRequest = z
  .object({
    countryCode: CountryCode,
    kind: TaxRuleKindSchema,
    hsnSac: z.string().trim().max(16).optional(),
    variantId: Id.optional(),
    rateBps: z.number().int().min(0).max(5000),
    inclusive: z.boolean().optional(),
    label: z.string().trim().max(120).optional(),
    active: z.boolean().optional(),
    validFrom: IsoDateTime.optional(),
    validTo: IsoDateTime.optional(),
  })
  .strict();
export type UpsertTaxRuleRequest = z.infer<typeof UpsertTaxRuleRequest>;

export const QuoteTaxRequest = z
  .object({
    countryCode: CountryCode,
    amountMinor: z.number().int().min(0),
    hsnSac: z.string().trim().max(16).optional(),
    variantId: Id.optional(),
    /// If true, treat `amountMinor` as tax-inclusive (the result strips out the tax portion).
    inclusive: z.boolean().optional(),
  })
  .strict();
export type QuoteTaxRequest = z.infer<typeof QuoteTaxRequest>;

export const TaxQuoteResultSchema = z.object({
  ruleId: Id.nullable(),
  rateBps: z.number().int(),
  netMinor: z.number().int(),
  taxMinor: z.number().int(),
  grossMinor: z.number().int(),
  label: z.string().nullable(),
});
export type TaxQuoteResult = z.infer<typeof TaxQuoteResultSchema>;

// Re-export for FE convenience.
export { Paise as MinorUnit } from '../common/primitives';
